import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFacetedMinMaxValues,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable
} from '@tanstack/react-table';

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import { Required } from 'components/form/FormItem.styled';
import Checkbox from 'components/global/Checkbox';
import ContextMenu from 'components/global/ContextMenu';
import Icon from 'components/global/Icon';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Radio from 'components/global/Radio';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useLocalStorage from 'hooks/useLocalStorage';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faAngleDown, faAngleRight, faAngleUp, faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';

import TableFloatFilter from './Filters/TableFloatFilter';
import TableMultiSelectionFilter from './Filters/TableMultiSelectionFilter';
import TableRatingFilter from './Filters/TableRatingFilter';
import TableHeaderFilter from './Header/TableHeaderFilter';
import TableMenu from './Header/TableMenu';
import AddButton from './HelperComponents/AddButton';
import DeleteButton from './HelperComponents/DeleteButton';
import EditButton from './HelperComponents/EditButton';
import * as S from './Table.styled';

const defaultColumn = {
	minSize: 30,
	maxSize: 500
};

const Table = (props, ref) => {
	const {
		data: dataProp = [],
		columns: columnsProp = [],
		visibleColumns: visibleColumnsProp,
		noRecordsFound,
		footerComponent,
		loading,
		selectedRows,
		onSelectedRowsChange,
		enableRowSelection = false,
		hideRowSelectionColumn = false,
		enableMultiRowSelection,
		onRowEdit,
		onRowEditEnd,
		onRowDelete,
		onRowAdd,
		onRowDrag,
		showAddNewRowButton = false,
		deletableLastRow = false,
		isRowDeletable,
		meta,
		initEditMode,
		filterable: filterableProp,
		storageKey,
		isTree = false,
		contextMenuItems,
		rowExpansion = false,
		showColumnsMenu = false,
		customExpansionGrid,
		defaultSort,
		showHeader
	} = props;

	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const isDraggable = !!onRowDrag;
	const filterable = filterableProp && !loading;

	const data = useMemo(
		() =>
			loading
				? Array.from({ length: 5 }, () => {
						const obj = {};
						columnsProp.forEach((col) => {
							obj[col.accessorKey] = undefined;
						});
						return obj;
					})
				: [...dataProp],
		[dataProp, columnsProp, loading]
	);

	const getRowCanExpand = useCallback(
		() => ((isMobile && !isTree) || rowExpansion ? true : undefined),
		[isMobile, rowExpansion, isTree]
	);

	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [selectedContextMenuRow, setSelectedContextMenuRow] = useState(null);

	const handleOnContextMenu = (event, row) => {
		if (!contextMenuItems) return;

		event.preventDefault();
		event.stopPropagation();
		setAnchorPoint({ x: event.pageX, y: event.pageY });
		setIsContextMenuOpen(true);
		setSelectedContextMenuRow(row);
	};

	const [expanded, setExpanded] = useState({});

	useImperativeHandle(
		ref,
		() => ({
			collapseExpandedRows() {
				setExpanded({});
			}
		}),
		[setExpanded]
	);

	const [columnFilters, setColumnFilters] = useState([]);
	const [storedColumns, setStoredColumns] = useLocalStorage(`${storageKey}_table_columns`, []);

	const fixedStoredColumns = useMemo(() => {
		const filteredStoredColumns = storedColumns.filter((col) =>
			columnsProp.find((findCol) =>
				findCol.id && col.id ? findCol.id === col.id : findCol.accessorKey === col.accessorKey
			)
		);

		if (JSON.stringify(filteredStoredColumns) !== JSON.stringify(storedColumns)) {
			setStoredColumns(filteredStoredColumns);
		}

		return filteredStoredColumns;
	}, [storedColumns]);

	const columns = useMemo(() => {
		let nextStoredColumnIndex = 0;

		return [
			...(enableRowSelection && !hideRowSelectionColumn
				? (enableMultiRowSelection ?? enableRowSelection)
					? [
							{
								id: 'select',
								header: ({ table }) => (
									<Checkbox
										checked={table.getIsAllRowsSelected()}
										indeterminate={table.getIsSomeRowsSelected()}
										onChange={table.getToggleAllRowsSelectedHandler()}
									/>
								),
								cell: ({ row }) => (
									<Checkbox
										checked={row.getIsSelected()}
										disabled={!row.getCanSelect()}
										indeterminate={row.getIsSomeSelected()}
										onChange={row.getToggleSelectedHandler()}
									/>
								),
								size: 30,
								minSize: 30,
								maxSize: 30,
								enableResizing: false,
								enableHiding: false
							}
						]
					: [
							{
								id: 'select',
								cell: ({ row }) => (
									<Radio
										checked={row.getIsSelected()}
										disabled={!row.getCanSelect()}
										onChange={row.getToggleSelectedHandler()}
										style={{ width: '2rem' }}
									/>
								),
								size: 30,
								minSize: 30,
								maxSize: 30,
								enableResizing: false,
								enableHiding: false
							}
						]
				: []),
			...(storageKey && fixedStoredColumns.length > 0
				? columnsProp.map((column) => {
						const columnData = fixedStoredColumns.find((col) =>
							col.id && column.id ? col.id === column.id : col.accessorKey === column.accessorKey
						);

						if (!columnData) {
							return column;
						}

						const nextStoredColumn = fixedStoredColumns[nextStoredColumnIndex];

						nextStoredColumnIndex += 1;
						const nextColumn = columnsProp.find((findCol) =>
							findCol.id && nextStoredColumn.id
								? findCol.id === nextStoredColumn.id
								: findCol.accessorKey === nextStoredColumn.accessorKey
						);

						return {
							...nextColumn,
							show: nextStoredColumn.show,
							size: nextStoredColumn.size
						};
					})
				: columnsProp)
		];
	}, [columnsProp, fixedStoredColumns]);

	const visibleColumns = useMemo(() => {
		const visibleColumnsTemplate = visibleColumnsProp ?? {};

		if (isMobile || (!isMobile && (rowExpansion || showColumnsMenu))) {
			columns.forEach((column) => {
				if (
					(isMobile && column.showOnMobile !== true) ||
					(!isMobile &&
						((rowExpansion && column.showOnExpansion === true) ||
							(showColumnsMenu && column.show === false)))
				) {
					const id = column.id ?? column.accessorKey ?? column.header;
					if (id === undefined) {
						AppLogger.error('Table: Column must have an id, accessorKey or header');
						throw new Error('Table: Column must have an id, accessorKey or header');
					}
					visibleColumnsTemplate[id] = false;
				}
			});
		}

		return visibleColumnsTemplate;
	}, [columns, visibleColumnsProp, isMobile]);

	const [columnVisibility, setColumnVisibility] = useState(visibleColumns);

	useUpdateEffect(() => {
		if (JSON.stringify(visibleColumns) !== JSON.stringify(columnVisibility)) {
			setColumnVisibility(visibleColumns);
		}
	}, [visibleColumns]);

	const initRowSelectionIds = {};
	selectedRows?.forEach((row) => {
		const index = data.findIndex((obj) => JSON.stringify(row) === JSON.stringify(obj));
		initRowSelectionIds[index] = true;
	});

	const [rowSelection, setRowSelection] = useState(initRowSelectionIds);
	const [sorting, setSorting] = useState(defaultSort ?? []);
	const [editing, setEditing] = useState([]);

	useEffect(() => {
		if (initEditMode) {
			setEditing(
				data.map((row, index) => {
					if (initEditMode(row)) return index;
				})
			);
		}
	}, [data]);

	useUpdateEffect(() => {
		const selectedData = Object.keys(rowSelection).map((key) => data[key]);
		onSelectedRowsChange && onSelectedRowsChange(selectedData);
	}, [rowSelection]);

	const columnPinning = useMemo(
		() =>
			isMobile
				? {}
				: {
						left: columns.map((column) => (column.pin === 'left' ? column.id : undefined)).filter(Boolean),
						right: columns.map((column) => (column.pin === 'right' ? column.id : undefined)).filter(Boolean)
					},
		[columns, isMobile]
	);

	const table = useReactTable({
		data,
		columns,
		defaultColumn,
		state: {
			sorting,
			rowSelection,
			expanded,
			columnVisibility,
			columnFilters,
			columnPinning
		},
		onColumnFiltersChange: setColumnFilters,
		meta,
		enableRowSelection,
		enableMultiRowSelection: enableMultiRowSelection ?? enableRowSelection,
		onRowSelectionChange: setRowSelection,
		onColumnVisibilityChange: setColumnVisibility,
		onSortingChange: setSorting,
		onExpandedChange: setExpanded,
		getSortedRowModel: getSortedRowModel(),
		getSubRows: isTree ? (row) => row.children : undefined,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getRowCanExpand,
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getFacetedMinMaxValues: getFacetedMinMaxValues(),
		enableColumnFilters: filterable,
		filterFns: {
			float: TableFloatFilter,
			multiSelection: TableMultiSelectionFilter,
			rating: TableRatingFilter
		}
	});

	const renderCell = (row, cell) => {
		if (loading) {
			return <Skeleton />;
		}

		const isRowEditing = editing.includes(row.index);

		if (cell.column.columnDef.id === 'editRow') {
			if (cell.column.columnDef.isVisible !== undefined && !cell.column.columnDef.isVisible(row)) {
				return;
			}

			return (
				<EditButton
					row={row}
					isRowEditing={isRowEditing}
					setEditing={setEditing}
					onRowEdit={onRowEdit}
					onRowEditEnd={onRowEditEnd}
				/>
			);
		}

		if (cell.column.columnDef.id === 'deleteRow') {
			if (cell.column.columnDef.isVisible !== undefined && !cell.column.columnDef.isVisible(row)) {
				return;
			}

			return (
				<DeleteButton
					row={row}
					setEditing={setEditing}
					onRowDelete={onRowDelete}
					disabled={isRowDeletable ? !isRowDeletable(row) : !deletableLastRow && data.length === 1}
				/>
			);
		}

		const cellObject = cell.column.columnDef.cell;
		const renderFn = cell.column.columnDef.renderCell;
		const editCell = isRowEditing ? cell.column.columnDef.editCell : undefined;
		const cellValue = cell.getValue();
		return !editCell && !renderFn && (typeof cellValue === 'string' || typeof cellValue === 'number') ? (
			<Typography>{cellValue}</Typography>
		) : (
			flexRender(editCell ?? renderFn ?? cellObject, cell.getContext())
		);
	};

	const renderExpandedRow = (cell, row, i) => {
		const column = cell.column.columnDef;
		const isRowEditing = editing.includes(row.index);

		if (
			column.showOnMobile ||
			(rowExpansion && !isMobile && !column.showOnExpansion) ||
			column.id === 'editRow' ||
			column.id === 'deleteRow' ||
			column.id === 'select'
		) {
			return;
		}

		if (loading) {
			return <Skeleton />;
		}

		const cellObject = column.cell;
		const headerObject = column.header;
		const renderFn = column.renderCell;
		const editCell = isRowEditing ? column.editCell : undefined;
		const cellValue = cell.getValue();

		return (
			<S.ExpandedColumnContainer key={`expanded_cell_${i}`}>
				<S.HeaderContainer className="expanded">
					<Typography noWrap>{flexRender(headerObject, cell.getContext())}</Typography>
				</S.HeaderContainer>
				{!editCell && !renderFn && (typeof cellValue === 'string' || typeof cellValue === 'number') ? (
					<Typography>{cellValue}</Typography>
				) : (
					flexRender(editCell ?? renderFn ?? cellObject, cell.getContext())
				)}
			</S.ExpandedColumnContainer>
		);
	};

	return (
		<S.Wrapper>
			<S.Container>
				<TableMenu
					table={table}
					filterable={filterable}
					columnFilters={columnFilters}
					setColumnFilters={setColumnFilters}
					showColumnsMenu={showColumnsMenu}
					columns={columns}
					storedColumns={fixedStoredColumns}
					setStoredColumns={setStoredColumns}
					storageKey={storageKey}
					showHeader={showHeader}
				/>
				<S.TableScrollContainer>
					<S.Table>
						<S.Thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<S.Tr key={`headerGroup_${headerGroup.id}`}>
									{isDraggable && (
										<S.Th
											colSpan={1}
											style={{
												width: '30px'
											}}
										/>
									)}
									{headerGroup.headers.map((header) => {
										const col = header.column;
										const columnData = col.columnDef;

										return (
											<S.Th
												key={`header_${header.id}`}
												colSpan={header.colSpan}
												style={{
													width: header.getSize(),
													...getCommonPinningStyles(col, true)
												}}>
												<S.HeaderWrapper>
													{header.isPlaceholder ? null : (
														<S.HeaderFilterContainer>
															<S.Header
																onClick={col.getToggleSortingHandler()}
																className={col.getCanSort() ? 'sortable' : ''}>
																{typeof columnData.header === 'string' ? (
																	<Typography fontWeight="700">
																		{t(columnData.header)}{' '}
																		<Required>
																			{columnData.required && '*'}
																		</Required>
																	</Typography>
																) : (
																	flexRender(columnData.header, header.getContext())
																)}
																{col.getIsSorted() && (
																	<Icon
																		icon={
																			{
																				asc: faAngleUp,
																				desc: faAngleDown
																			}[col.getIsSorted()]
																		}
																		color="var(--black)"
																	/>
																)}
															</S.Header>
															{filterable && col.getCanFilter() ? (
																<div>
																	<TableHeaderFilter column={col} />
																</div>
															) : null}
														</S.HeaderFilterContainer>
													)}
												</S.HeaderWrapper>
												<S.Resizer
													onMouseDown={header.getResizeHandler()}
													onTouchEnd={header.getResizeHandler()}
													className={`resizer ${col.getIsResizing() ? 'isResizing' : ''}`}
												/>
											</S.Th>
										);
									})}
								</S.Tr>
							))}
						</S.Thead>
						<DndProvider backend={HTML5Backend}>
							<S.Tbody>
								{table.getRowModel().rows.map((row, i) => (
									<React.Fragment key={`rows_${i}`}>
										{isDraggable ? (
											<DraggableTableRow
												row={row}
												renderCell={renderCell}
												isMobile={isMobile}
												rowExpansion={rowExpansion}
												isTree={isTree}
												onRowDrag={onRowDrag}
												onContextMenu={contextMenuItems && handleOnContextMenu}
												selectedContextMenuRowID={selectedContextMenuRow?.original.id}
											/>
										) : (
											<TableRow
												row={row}
												renderCell={renderCell}
												isMobile={isMobile}
												rowExpansion={rowExpansion}
												isTree={isTree}
												onContextMenu={contextMenuItems && handleOnContextMenu}
												selectedContextMenuRowID={selectedContextMenuRow?.original.id}
												enableRowSelection={enableRowSelection}
											/>
										)}
										{(isMobile || rowExpansion) &&
										!isTree &&
										row.getIsExpanded() &&
										row
											.getAllCells()
											.map((cell, i) => renderExpandedRow(cell, row, i))
											.join('') !== '' ? (
											<S.Tr
												key={`expanded_row_${i}`}
												className={`${contextMenuItems ? 'context-menu' : ''} ${
													selectedContextMenuRow?.original?.id
														? selectedContextMenuRow?.original?.id === row.original?.id &&
															'selected'
														: ''
												}`}
												onContextMenu={(e) => handleOnContextMenu(e, row)}>
												<S.Td className="expanded" colSpan={row.getVisibleCells().length}>
													<S.ExpandedRowContainer
														className={editing.includes(row.index) ? 'edit-mode' : ''}
														customExpansionGrid={customExpansionGrid}>
														{row
															.getAllCells()
															.map((cell, i) => renderExpandedRow(cell, row, i))}
													</S.ExpandedRowContainer>
												</S.Td>
											</S.Tr>
										) : null}
									</React.Fragment>
								))}
							</S.Tbody>
						</DndProvider>
					</S.Table>
					{data.length === 0 ? noRecordsFound ? noRecordsFound : <NoRecordsFound /> : undefined}
					{data.length > 0 && table.getRowModel().rows.length === 0 && (
						<NoRecordsFound subTitle="table.noFilteredDataFound" />
					)}
					{showAddNewRowButton && (
						<AddButton
							onRowAdd={onRowAdd}
							setEditing={setEditing}
							setExpanded={setExpanded}
							addIndex={data.length}
						/>
					)}
					{footerComponent ? footerComponent : undefined}
					{!loading && contextMenuItems && isContextMenuOpen && (
						<ContextMenu
							menuItems={contextMenuItems}
							row={selectedContextMenuRow}
							anchorPoint={anchorPoint}
							onClose={() => {
								setIsContextMenuOpen(false);
								setSelectedContextMenuRow(null);
							}}
							isContextMenuOpen={isContextMenuOpen}
							setIsContextMenuOpen={setIsContextMenuOpen}
						/>
					)}
				</S.TableScrollContainer>
			</S.Container>
		</S.Wrapper>
	);
};

const getCommonPinningStyles = (column, isHeader) => {
	const isPinned = column.getIsPinned();
	const isRTL = document.body.dir === 'rtl';

	const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
	const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

	return {
		borderInlineStart: isFirstRightPinnedColumn ? '1px solid var(--border-color)' : undefined,
		borderInlineEnd: isLastLeftPinnedColumn ? '1px solid var(--border-color)' : undefined,
		...(isPinned === 'left' && { [isRTL ? 'right' : 'left']: `${column.getStart('left')}px` }),
		...(isPinned === 'right' && { [isRTL ? 'left' : 'right']: `${column.getAfter('right')}px` }),
		position: isPinned ? 'sticky' : 'relative',
		width: column.getSize(),
		zIndex: isPinned ? 1 : 0,
		backgroundColor: isHeader ? 'var(--background-3)' : 'var(--white)'
	};
};

const DraggableTableRow = ({
	row,
	renderCell,
	isMobile,
	isTree,
	rowExpansion,
	onRowDrag,
	onContextMenu,
	selectedContextMenuRowID
}) => {
	const [, dropRef] = useDrop({
		accept: 'row',
		drop: (draggedRow) => onRowDrag(draggedRow.index, row.index)
	});

	const [{ isDragging }, dragRef, previewRef] = useDrag({
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		}),
		item: () => row,
		type: 'row'
	});

	return (
		<S.Tr
			key={`row_${row.id}`}
			ref={previewRef}
			className={`${onContextMenu ? 'context-menu' : ''} ${
				selectedContextMenuRowID ? selectedContextMenuRowID === row.original?.id && 'selected' : ''
			}`}
			onContextMenu={onContextMenu ? (e) => onContextMenu(e, row) : undefined}
			style={{ opacity: isDragging ? 0.5 : 1 }}>
			<S.Td
				ref={dropRef}
				style={{
					width: '30px'
				}}>
				<Icon ref={dragRef} icon={faGripDotsVertical} size="large" color="var(--black)" />
			</S.Td>
			<BaseRow
				row={row}
				renderCell={renderCell}
				isMobile={isMobile}
				isTree={isTree}
				rowExpansion={rowExpansion}
			/>
		</S.Tr>
	);
};

const TableRow = ({
	row,
	renderCell,
	isMobile,
	isTree,
	rowExpansion,
	onContextMenu,
	selectedContextMenuRowID,
	enableRowSelection
}) => (
	<S.Tr
		key={`row_${row.id}`}
		className={`${onContextMenu ? 'context-menu' : ''} ${
			selectedContextMenuRowID ? selectedContextMenuRowID === row.original?.id && 'selected' : ''
		}`}
		onContextMenu={onContextMenu ? (e) => onContextMenu(e, row) : undefined}>
		{isTree ? (
			<TreeRow row={row} renderCell={renderCell} isMobile={isMobile} />
		) : (
			<BaseRow
				row={row}
				renderCell={renderCell}
				isMobile={isMobile}
				rowExpansion={rowExpansion}
				enableRowSelection={enableRowSelection}
			/>
		)}
	</S.Tr>
);

const BaseRow = ({ row, renderCell, isMobile, rowExpansion, enableRowSelection }) =>
	row.getVisibleCells().map((cell, i) => (
		<S.Td
			key={`cell_${cell.id}`}
			style={{
				width: cell.column.getSize(),
				...getCommonPinningStyles(cell.column)
			}}>
			<S.CellContainer>
				{renderCell(row, cell)}
				{(isMobile || rowExpansion) && (enableRowSelection ? i === 1 : i === 0) && (
					<Button
						onClick={row.getToggleExpandedHandler()}
						icon={row.getIsExpanded() ? faAngleUp : faAngleDown}
						variant="borderless"
						size={isMobile ? 'x-small' : 'small'}
						iconSize={isMobile ? 'x-small' : 'medium'}
						iconButton
						style={{ overflow: 'hidden' }}
					/>
				)}
			</S.CellContainer>
		</S.Td>
	));

const TreeRow = ({ row, renderCell, isMobile }) =>
	row.getVisibleCells().map((cell, i) => {
		const parentRow = row.getParentRow();
		const siblingRows = parentRow?.subRows;
		const isLastRow = siblingRows?.[siblingRows?.length - 1].id === row.id;
		const renderLine = row.depth > 0 && i === 0;
		const paddingLength = renderLine ? row.depth * 2 + 3.5 : 0;
		const marginLength = renderLine ? (row.depth - 1) * 2 + 4 : 0;

		const isAllParentsLastRow = (row) => {
			if (row.depth === 0) return true;
			const parentRow = row.getParentRow();
			const siblingRows = parentRow?.subRows;
			const isLastRow = siblingRows?.[siblingRows?.length - 1].id === row.id;

			if (parentRow) {
				return isLastRow && isAllParentsLastRow(parentRow);
			}

			return isLastRow;
		};

		return (
			<S.Td
				key={`cell_${cell.id}`}
				className={`tree-cell ${
					renderLine &&
					((row.depth === 1 && isLastRow && !row.getIsExpanded()) ||
					(row.depth > 1 && isLastRow && isAllParentsLastRow(row))
						? 'halfLeftBorder'
						: 'leftBorder')
				}`}
				style={{
					width: cell.column.getSize()
				}}>
				<S.CellContainer padding={paddingLength} expandable={row.getCanExpand()}>
					{marginLength > 0 && <S.TreeBottomLine margin={marginLength} expandable={row.getCanExpand()} />}
					{row.getCanExpand() && i === 0 && (
						<Button
							onClick={row.getToggleExpandedHandler()}
							variant="transparent"
							size={isMobile ? 'x-small' : 'small'}
							iconSize={isMobile ? 'x-small' : 'medium'}
							iconButton
							style={{ overflow: 'hidden' }}>
							<Icon
								icon={faAngleRight}
								color="var(--blue)"
								rotation={row.getIsExpanded() ? 90 : document.body.dir === 'rtl' ? 180 : 0}
							/>
						</Button>
					)}
					{renderCell(row, cell)}
				</S.CellContainer>
			</S.Td>
		);
	});

export default forwardRef(Table);
